// src/ui/FicheTray.ts

import Phaser from 'phaser';
import type { Rarity } from '../state/types';
import { addCrispText } from './textHelper';
import { ficheSpritesheet } from '../content/fiches';

/**
 * Phase 14: unicode chess glyphs for chess fiche def IDs.
 * Used in the no-texture fallback path in draw() to render the primary
 * visual identifier (24px glyph) instead of the default chips label header.
 */
const CHESS_GLYPHS: Readonly<Record<string, string>> = {
  chess_pawn: '♙', // ♙
  chess_knight: '♘', // ♘
  chess_bishop: '♗', // ♗
  chess_rook: '♖', // ♖
  chess_queen: '♕', // ♕
  chess_king: '♔', // ♔
};

// Re-export Rarity here so existing consumers that import it from FicheTray
// keep compiling; the canonical alias lives in src/state/types.ts (Phase 12).
export type { Rarity };

export interface FicheSlot {
  id: string;
  defId: string;
  chips: number;
  rarity: Rarity;
  name: string;
}

/** A game object paired with its "resting" alpha so markUsed() can restore
 *  to the correct baseline rather than unconditionally setting 1. Needed for
 *  the legendary halo arc whose rest alpha is 0.55 (FI-01 fix). */
interface AlphaTarget {
  obj: { setAlpha(value: number): unknown };
  restAlpha: number;
}

interface TraySlot {
  ficheId: string | null;
  circle: Phaser.GameObjects.Arc | null;
  rarity: Rarity | null; // ADD — needed to build ghost with correct color
  alphaTargets: AlphaTarget[];
  idx: number;
}

export class FicheTray extends Phaser.GameObjects.Container {
  private slots: TraySlot[] = [];

  /** Called with the ficheId when the player right-clicks a tray slot to pass/un-pass it. */
  public onPass?: (ficheId: string) => void;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.draw([]);
  }

  /** Render fiche slots. traySize controls how many slots to show (filled + empty). */
  draw(fiches: ReadonlyArray<FicheSlot>, traySize?: number): void {
    this.removeAll(true);
    this.slots = [];
    const slotW = 80;
    const slotH = 80;
    const gap = 10;
    const capacity = traySize ?? fiches.length;
    // Centre the tray horizontally within the scene width
    const totalW = capacity * (slotW + gap) - gap;
    const sceneW = this.scene.scale.width;
    const startX = Math.round((sceneW - totalW) / 2);
    for (let i = 0; i < capacity; i++) {
      const sx = startX + i * (slotW + gap);
      const cx = sx + slotW / 2;
      const cy = slotH / 2;
      const isFilled = i < fiches.length;
      if (isFilled) {
        const fiche = fiches[i]!;
        const fillColor = rarityColor(fiche.rarity);
        const circle = this.scene.add
          .circle(cx, cy, slotW / 2 - 4, fillColor)
          .setStrokeStyle(2, 0x000000);
        // FI-01 fix: alphaTargets tracks { obj, restAlpha } pairs so markUsed()
        // restores each object to its own baseline (e.g. 0.55 for the halo arc)
        // instead of unconditionally resetting everything to alpha=1.
        const alphaTargets: AlphaTarget[] = [{ obj: circle, restAlpha: 1 }];

        {
          const ficheKey = `fiche-${fiche.defId}`;
          const sheet = ficheSpritesheet(fiche.defId);
          const hasSheet = sheet !== undefined && this.scene.textures.exists(sheet.key);
          if (hasSheet || this.scene.textures.exists(ficheKey)) {
            const img = hasSheet
              ? this.scene.add.image(cx, cy, sheet!.key, sheet!.frame).setDisplaySize(slotW, slotH)
              : this.scene.add.image(cx, cy, ficheKey).setDisplaySize(slotW, slotH);
            const badge = addCrispText(this.scene, cx + 20, cy + 20, String(fiche.chips), {
              color: '#ffffff',
              fontSize: '13px',
              stroke: '#000000',
              strokeThickness: 3,
            }).setOrigin(0.5);
            this.add([circle, img, badge]);
            alphaTargets.push({ obj: img, restAlpha: 1 }, { obj: badge, restAlpha: 1 });
          } else {
            const glyph = CHESS_GLYPHS[fiche.defId];
            if (glyph) {
              // Chess fiche: render glyph at top, chips label below, name at bottom.
              const textColor = glyphTextColor(fiche.rarity);
              const glyphLabel = addCrispText(this.scene, cx, cy - 10, glyph, {
                color: textColor,
                fontSize: '34px',
              }).setOrigin(0.5);
              const chipsLabel = addCrispText(this.scene, cx, cy + 16, String(fiche.chips), {
                color: textColor,
                fontSize: '14px',
              }).setOrigin(0.5);
              const nameLabel = addCrispText(this.scene, cx, cy + 20, ficheShortLabel(fiche.name), {
                color: '#333333',
                fontSize: '7px',
              }).setOrigin(0.5);

              // Legendary halo: secondary concentric white ring (chess_queen only in Phase 14).
              // Per UI-SPEC §Legendary fiche ring: radius 22px, stroke 1px #ffffff alpha 0.55.
              // FI-01 fix: restAlpha=0.55 preserves the faint-halo baseline after markUsed restore.
              const toAdd: Phaser.GameObjects.GameObject[] = [
                circle,
                glyphLabel,
                chipsLabel,
                nameLabel,
              ];
              if (fiche.rarity === 'legendary') {
                const halo = this.scene.add
                  .arc(cx, cy, 22, 0, 360)
                  .setFillStyle(0, 0) // transparent fill
                  .setStrokeStyle(1, 0xffffff)
                  .setAlpha(0.55);
                toAdd.push(halo);
                alphaTargets.push({ obj: halo, restAlpha: 0.55 });
              }

              this.add(toAdd);
              alphaTargets.push(
                { obj: glyphLabel, restAlpha: 1 },
                { obj: chipsLabel, restAlpha: 1 },
                { obj: nameLabel, restAlpha: 1 },
              );
            } else {
              // Phase 17 (FICHE-14): chip rarity inner motif when texture loaded
              const chipKey = `chip-${fiche.rarity}`;
              if (this.scene.textures.exists(chipKey)) {
                const motif = this.scene.add
                  .image(cx, cy - 2, chipKey)
                  .setDisplaySize(slotW - 8, slotH - 8) // 72×72 inside arc ring
                  .setAlpha(0.9);
                const badge = addCrispText(this.scene, cx + 20, cy + 20, String(fiche.chips), {
                  color: '#ffffff',
                  fontSize: '13px',
                  stroke: '#000000',
                  strokeThickness: 3,
                }).setOrigin(0.5);
                this.add([circle, motif, badge]);
                alphaTargets.push({ obj: motif, restAlpha: 0.9 }, { obj: badge, restAlpha: 1 });
              } else {
                // Shape-only fallback: chips label + name label
                const chipsLabel = addCrispText(this.scene, cx, cy - 4, String(fiche.chips), {
                  color: '#000000',
                  fontSize: '12px',
                }).setOrigin(0.5);
                const nameLabel = addCrispText(
                  this.scene,
                  cx,
                  cy + 9,
                  ficheShortLabel(fiche.name),
                  {
                    color: '#333333',
                    fontSize: '7px',
                  },
                ).setOrigin(0.5);
                this.add([circle, chipsLabel, nameLabel]);
                alphaTargets.push(
                  { obj: chipsLabel, restAlpha: 1 },
                  { obj: nameLabel, restAlpha: 1 },
                );
              }
            }
          } // close else (no per-fiche sprite)
        }
        this.slots.push({ ficheId: fiche.id, circle, rarity: fiche.rarity, alphaTargets, idx: i });
      } else {
        const empty = this.scene.add
          .circle(cx, cy, slotW / 2 - 4, 0x0a3d20)
          .setStrokeStyle(1, 0x666666);
        this.add([empty]);
        this.slots.push({ ficheId: null, circle: null, rarity: null, alphaTargets: [], idx: i });
      }
    }
  }

  /**
   * Wire pointerdown on every filled slot to begin a drag gesture.
   * On pointerdown the slot dims to 0.35 alpha (same as markUsed treatment)
   * and a pre-built ghost Arc is handed to the callback so RoundScene can
   * track it during pointermove/pointerup.
   * Re-call after each draw() since slot circles are recreated.
   */
  public setOnFicheDragStart(
    cb: (ficheId: string, idx: number, ghost: Phaser.GameObjects.Arc) => void,
  ): void {
    this.slots.forEach((slot) => {
      if (!slot.ficheId || !slot.circle || slot.rarity === null) return;
      const id = slot.ficheId;
      const idx = slot.idx;
      const rarity = slot.rarity;
      slot.circle.setInteractive({ useHandCursor: true });
      slot.circle.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
        if (ptr.rightButtonDown()) {
          // Right-click: toggle pass/un-pass — do NOT start a drag.
          if (this.onPass) this.onPass(id);
          return;
        }
        // Dim the dragged slot (same factor as markUsed for a placed fiche)
        slot.alphaTargets.forEach(({ obj, restAlpha }) => obj.setAlpha(restAlpha * 0.35));
        // Build ghost: circle cloned from the slot visual, positioned at pointer,
        // depth 25 (above spin button at depth 20 per CONTEXT.md specifics).
        const ghost = this.scene.add
          .circle(ptr.worldX, ptr.worldY, 36, rarityColor(rarity))
          .setStrokeStyle(2, 0x000000)
          .setAlpha(0.75)
          .setDepth(25);
        cb(id, idx, ghost);
      });
    });
  }

  /** World-space x of the centre of slot idx (used for score-pop anchoring). */
  public slotCenterX(idx: number, capacity: number): number {
    const slotW = 80;
    const gap = 10;
    const totalW = capacity * (slotW + gap) - gap;
    const startX = Math.round((this.scene.scale.width - totalW) / 2);
    return this.x + startX + idx * (slotW + gap) + slotW / 2;
  }

  /** World-space y of the centre of any slot in the tray (all slots share the same y). */
  public slotCenterY(): number {
    return this.y + 40; // slotH=80, so vertical centre is y+40
  }

  /** Grey out placed fiches (alpha 0.35). Pass [] to restore all.
   *
   * FI-01 fix: each AlphaTarget carries its own restAlpha baseline so objects
   * like the legendary halo arc (restAlpha=0.55) are restored to 0.55 rather
   * than unconditionally reset to 1. dim factor is 0.35 when placed, 1 otherwise.
   */
  public markUsed(usedIds: ReadonlyArray<string>): void {
    const used = new Set(usedIds);
    this.slots.forEach((slot) => {
      if (!slot.ficheId) return;
      const factor = used.has(slot.ficheId) ? 0.35 : 1;
      slot.alphaTargets.forEach(({ obj, restAlpha }) => {
        obj.setAlpha(restAlpha * factor);
      });
    });
  }

  /** Dim passed (skipped) fiches at 0.35 alpha, same as placed. Separate from
   *  markUsed so callers can combine both sets independently. */
  public markPassed(passedIds: ReadonlyArray<string>): void {
    const passed = new Set(passedIds);
    this.slots.forEach((slot) => {
      if (!slot.ficheId) return;
      if (!passed.has(slot.ficheId)) return; // markUsed handles the rest
      slot.alphaTargets.forEach(({ obj, restAlpha }) => {
        obj.setAlpha(restAlpha * 0.35);
      });
    });
  }
}

function rarityColor(r: Rarity): number {
  // Phase 12: legendary added (gold). v1 fiches retain their pre-existing
  // rarities — `legendary` only renders when Phase 14+ defs land.
  if (r === 'legendary') return 0xd4af37; // Phase 17: Art Deco gold
  if (r === 'rare') return 0xa259d6;
  if (r === 'uncommon') return 0x4a90e2;
  return 0xffffff;
}

/**
 * Phase 14: text color for glyph and chips labels on chess fiche circles.
 * Per UI-SPEC §Token Layout: light backgrounds (#ffffff, #ffd700) → black text;
 * dark backgrounds (#4a90e2, #a259d6) → white text.
 */
function glyphTextColor(r: Rarity): string {
  if (r === 'uncommon' || r === 'rare') return '#ffffff';
  return '#000000'; // common (#ffffff) and legendary (#ffd700) → black
}

const FICHE_SHORT: Readonly<Record<string, string>> = {
  Standard: 'Std',
  'Lucky 7': 'L7',
  'Red Hot': 'Red',
  'Cold Steel': 'Cold',
  'Even Steven': 'Even',
  Gambler: 'Gamb',
  "Pair o' Dice": 'PoD',
  'House Special': 'Hse',
  // Phase 14 chess fiches:
  'Chess Pawn': 'Pawn',
  'Chess Knight': 'Knt',
  'Chess Bishop': 'Bsh',
  'Chess Rook': 'Rook',
  'Chess Queen': 'Quen',
  'Chess King': 'King',
};

function ficheShortLabel(name: string): string {
  return FICHE_SHORT[name] ?? name.slice(0, 4);
}
